import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { usePermissions, PAGE_LABELS } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Plus, X } from "lucide-react";

const BUILTIN = ["distributor", "tabteam"];

export function RolePermissions() {
  const { pages, roles, labels, options, reload } = usePermissions();
  const [draft, setDraft] = useState(null);
  const [newRole, setNewRole] = useState("");
  useEffect(() => {
    if (roles && Object.keys(roles).length) setDraft({ roles: JSON.parse(JSON.stringify(roles)), labels: { ...(labels || {}) }, options: JSON.parse(JSON.stringify(options || {})) });
  }, [roles, labels, options]);
  if (!draft) return null;

  const cols = [...BUILTIN, ...Object.keys(draft.labels).filter((k) => !BUILTIN.includes(k))];
  const toggle = (role, page) => setDraft((d) => {
    const set = new Set(d.roles[role] || []);
    set.has(page) ? set.delete(page) : set.add(page);
    return { ...d, roles: { ...d.roles, [role]: pages.filter((p) => set.has(p)) } };
  });
  const toggleOpt = (role, key) => setDraft((d) => ({ ...d, options: { ...d.options, [role]: { ...(d.options[role] || {}), [key]: !(d.options[role] || {})[key] } } }));
  const addRole = () => {
    const label = newRole.trim();
    if (!label) return;
    const key = label.toLowerCase().replace(/[çğıöşü]/g, (c) => ({ ç: "c", ğ: "g", ı: "i", ö: "o", ş: "s", ü: "u" }[c])).replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30) || `rol_${Date.now()}`;
    if (draft.labels[key] || BUILTIN.includes(key)) return toast.error("Bu isimde bir rol zaten var");
    setDraft((d) => ({ ...d, labels: { ...d.labels, [key]: label }, roles: { ...d.roles, [key]: [...pages] }, options: { ...d.options, [key]: { no_stock_deduction: false } } }));
    setNewRole("");
  };
  const removeRole = (key) => {
    if (!window.confirm(`"${draft.labels[key]}" rolü silinsin mi? Bu roldeki kullanıcılar Distribütör olur.`)) return;
    setDraft((d) => { const labels = { ...d.labels }; delete labels[key]; const roles = { ...d.roles }; delete roles[key]; const options = { ...d.options }; delete options[key]; return { labels, roles, options }; });
  };
  const save = async () => {
    try {
      const custom = Object.fromEntries(Object.entries(draft.labels).filter(([k]) => !BUILTIN.includes(k)));
      const r = await api.put("/permissions", { roles: draft.roles, labels: custom, options: draft.options });
      toast.success(r.data.reassigned_users ? `Yetkiler kaydedildi · ${r.data.reassigned_users} kullanıcı Distribütör rolüne alındı` : "Yetkiler kaydedildi");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="role-permissions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-lg font-semibold"><ShieldCheck className="h-5 w-5 text-primary" /> Rol Yetkileri</h3>
          <p className="text-xs text-muted-foreground">Her rolün görebileceği sayfaları işaretleyin, kendi rolünüzü oluşturup kullanıcılara atayın. Admin her zaman tüm sayfaları görür.</p>
        </div>
        <Button data-testid="save-permissions-btn" onClick={save} className="font-semibold">Yetkileri Kaydet</Button>
      </div>
      <div className="mt-3 flex max-w-md items-center gap-2">
        <Input placeholder="Yeni rol adı (ör. Kasiyer)" value={newRole} onChange={(e) => setNewRole(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRole()} data-testid="new-role-name" />
        <Button variant="secondary" onClick={addRole} data-testid="add-role-btn"><Plus className="mr-1 h-4 w-4" /> Rol Ekle</Button>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2 pr-4">Sayfa</th>
              {cols.map((r) => (
                <th key={r} className="py-2 text-center">
                  <span className="inline-flex items-center gap-1">{draft.labels[r] || r}
                    {!BUILTIN.includes(r) && <button type="button" className="text-muted-foreground hover:text-destructive" title="Rolü sil" data-testid={`remove-role-${r}`} onClick={() => removeRole(r)}><X className="h-3.5 w-3.5" /></button>}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p} className="border-b border-border/60 last:border-0">
                <td className="py-2 pr-4">{PAGE_LABELS[p] || p}</td>
                {cols.map((r) => (
                  <td key={r} className="py-2 text-center">
                    <Checkbox data-testid={`perm-${r}-${p}`} checked={(draft.roles[r] || []).includes(p)} onCheckedChange={() => toggle(r, p)} />
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-muted/30">
              <td className="py-2 pr-4 font-medium">Satışta stok <u>düşmesin</u><p className="text-[11px] font-normal text-muted-foreground">Yalnızca Kasa & Satış için geçerli; perakende paket satışları her zaman stoktan düşer</p></td>
              {cols.map((r) => (
                <td key={r} className="py-2 text-center">
                  <Checkbox data-testid={`opt-${r}-no_stock_deduction`} checked={!!(draft.options[r] || {}).no_stock_deduction} onCheckedChange={() => toggleOpt(r, "no_stock_deduction")} />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
